import type * as React from "react";

import { cn } from "@/lib/utils";

/**
 * A card is a container, not a control. Attaching a click handler here makes it
 * unreachable by keyboard, so those props are removed from the type: use
 * `CardOverlayButton` instead.
 */
type CardProps = Omit<React.ComponentProps<"div">, "onClick" | "onKeyDown">;

function Card({ className, ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      className={cn(
        // `relative` so CardOverlayButton stretches against the card.
        "bg-card text-card-foreground relative flex flex-col gap-6 rounded-xl border py-6 shadow-sm",
        className,
      )}
      {...props}
    />
  );
}

/**
 * A transparent button stretched over an entire Card, making the whole card one
 * click target with a focus ring that follows its corners.
 *
 * Must be a *direct child* of `Card`: it positions against the nearest
 * positioned ancestor, so an intermediate `relative` (`CardHeader`, say) would
 * shrink it to that section. Put it first so it leads the tab order, give it an
 * `aria-label` rather than wrapping the card's content — a button around the
 * whole card would suppress its heading — and mark anything that must stay
 * interactive above it with `relative z-20`.
 *
 * The ring is inset because cards clip their overflow, which would otherwise
 * cut an outward ring off entirely.
 */
function CardOverlayButton({
  className,
  ...props
}: React.ComponentProps<"button"> & { "aria-label": string }) {
  return (
    <button
      type="button"
      data-slot="card-overlay-button"
      className={cn(
        "absolute inset-0 z-10 rounded-xl outline-none focus-ring-inset",
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      // `relative` keeps footer content clickable above a stretched CARD_LINK.
      className={cn(
        "relative flex items-center px-6 [.border-t]:pt-6",
        className,
      )}
      {...props}
    />
  );
}

export {
  Card,
  CardOverlayButton,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
